import React, { useEffect, useState } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemText,
  Chip,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Campaign as CampaignIcon,
  Article as ArticleIcon,
  Add as AddIcon,
  AccountTree as WorkflowIcon,
  AutoAwesome as AIIcon,
  Search as SearchIcon,
  HealthAndSafety as HealthIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { leadsService } from '../services/leads';
import { campaignsService } from '../services/campaigns';
import { contentService } from '../services/content';
import SystemHealth from '../components/SystemHealth';
import axios from 'axios';
import { getCurrentWorkspaceId } from '../utils/workspace';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalLeads: 0,
    activeCampaigns: 0,
    contentGenerated: 0,
    conversionRate: '0%',
    totalWorkflows: 0,
    runningWorkflows: 0,
    completedWorkflows: 0,
  });
  const [recentActivity, setRecentActivity] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const workspaceId = getCurrentWorkspaceId();
      const [leadsRes, campaignsRes, contentRes, workflowsRes] = await Promise.all([
        leadsService.getLeads({}),
        campaignsService.getCampaigns(),
        contentService.getContent(),
        axios.get(`${API_URL}/workflows`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
        }).catch(() => ({ data: [] })),
      ]);

      const workflows = workflowsRes.data || [];
      setStats({
        totalLeads: leadsRes.total || 0,
        activeCampaigns: campaignsRes.items?.filter((c: any) => c.status === 'active').length || 0,
        contentGenerated: contentRes.total || 0,
        conversionRate: '12.5%',
        totalWorkflows: workflows.length,
        runningWorkflows: workflows.filter((w: any) => w.status === 'running').length,
        completedWorkflows: workflows.filter((w: any) => w.status === 'completed').length,
      });

      const activities = [
        ...(workflows.slice(0, 2).map((w: any) => ({
          type: 'workflow',
          text: `Workflow "${w.name || w.goal}" ${w.status}`,
          time: 'Recently',
        })) || []),
        ...(campaignsRes.items?.slice(0, 2).map((c: any) => ({
          type: 'campaign',
          text: `Campaign "${c.name}" ${c.status === 'active' ? 'is active' : 'created'}`,
          time: 'Recently',
        })) || []),
        ...(contentRes.items?.slice(0, 1).map((c: any) => ({
          type: 'content',
          text: `Content generated for ${c.platform}`,
          time: 'Recently',
        })) || []),
      ];
      setRecentActivity(activities.slice(0, 5));
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { title: 'Total Leads', value: stats.totalLeads.toString(), icon: <PeopleIcon fontSize="large" />, color: '#1976d2', onClick: () => navigate('/leads') },
    { title: 'Active Campaigns', value: stats.activeCampaigns.toString(), icon: <CampaignIcon fontSize="large" />, color: '#dc004e', onClick: () => navigate('/campaigns') },
    { title: 'Content Generated', value: stats.contentGenerated.toString(), icon: <ArticleIcon fontSize="large" />, color: '#388e3c', onClick: () => navigate('/content') },
    { title: 'Conversion Rate', value: stats.conversionRate, icon: <TrendingUpIcon fontSize="large" />, color: '#f57c00', onClick: () => navigate('/analytics') },
  ];

  const workflowCards = [
    { title: 'Total Workflows', value: stats.totalWorkflows.toString(), icon: <WorkflowIcon fontSize="large" />, color: '#9c27b0', onClick: () => navigate('/workflows') },
    { title: 'Running Workflows', value: stats.runningWorkflows.toString(), icon: <WorkflowIcon fontSize="large" />, color: '#ff9800', onClick: () => navigate('/workflows') },
    { title: 'Completed Today', value: stats.completedWorkflows.toString(), icon: <WorkflowIcon fontSize="large" />, color: '#4caf50', onClick: () => navigate('/workflows') },
  ];

  return (
    <Box sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 0 } }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: { xs: 2, sm: 3 },
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 1, sm: 0 }
      }}>
        <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
          Dashboard
        </Typography>
      </Box>
      <Grid container spacing={{ xs: 2, sm: 3 }}>
        {statCards.map((stat) => (
          <Grid item xs={12} sm={6} md={3} key={stat.title}>
            <Card 
              sx={{ cursor: 'pointer', '&:hover': { boxShadow: 3 } }}
              onClick={stat.onClick}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" gutterBottom>
                      {stat.title}
                    </Typography>
                    <Typography variant="h4">
                      {stat.value}
                    </Typography>
                  </Box>
                  <Box sx={{ color: stat.color }}>
                    {stat.icon}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* AI Workflow Status Section */}
      <Box sx={{ mt: { xs: 3, sm: 4 }, mb: 2 }}>
        <Typography 
          variant="h5" 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            fontSize: { xs: '1.25rem', sm: '1.5rem' }
          }}
        >
          <AIIcon color="secondary" />
          Multi-Agent Workflows
        </Typography>
      </Box>
      <Grid container spacing={3}>
        {workflowCards.map((stat) => (
          <Grid item xs={12} sm={6} md={4} key={stat.title}>
            <Card 
              sx={{ cursor: 'pointer', '&:hover': { boxShadow: 3 } }}
              onClick={stat.onClick}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" gutterBottom>
                      {stat.title}
                    </Typography>
                    <Typography variant="h4">
                      {stat.value}
                    </Typography>
                  </Box>
                  <Box sx={{ color: stat.color }}>
                    {stat.icon}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Recent Activity
            </Typography>
            {loading ? (
              <Typography color="textSecondary">Loading...</Typography>
            ) : recentActivity.length > 0 ? (
              <List>
                {recentActivity.map((activity, index) => (
                  <ListItem key={index} divider={index < recentActivity.length - 1}>
                    <ListItemText 
                      primary={activity.text}
                      secondary={activity.time}
                    />
                    <Chip 
                      label={activity.type} 
                      size="small" 
                      color={
                        activity.type === 'campaign' ? 'primary' : 
                        activity.type === 'workflow' ? 'secondary' : 
                        'default'
                      }
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography color="textSecondary">No recent activity</Typography>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              AI Quick Actions
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <Button
                variant="contained"
                startIcon={<AIIcon />}
                onClick={() => navigate('/campaigns')}
                fullWidth
                color="secondary"
              >
                Generate Campaign
              </Button>
              <Button
                variant="outlined"
                startIcon={<SearchIcon />}
                onClick={() => navigate('/leads')}
                fullWidth
              >
                Discover Leads
              </Button>
              <Button
                variant="outlined"
                startIcon={<ArticleIcon />}
                onClick={() => navigate('/content')}
                fullWidth
              >
                Generate Content
              </Button>
              <Button
                variant="outlined"
                startIcon={<WorkflowIcon />}
                onClick={() => navigate('/workflows')}
                fullWidth
              >
                View All Workflows
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* System Health Section */}
      <Box sx={{ mt: 4 }}>
        <Typography
          variant="h5"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 2,
            fontSize: { xs: '1.25rem', sm: '1.5rem' }
          }}
        >
          <HealthIcon color="success" />
          System Health
        </Typography>
        <Paper sx={{ p: 3 }}>
          <SystemHealth compact={false} autoRefresh={true} refreshInterval={10000} />
        </Paper>
      </Box>
    </Box>
  );
}
